import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BookHeart,
  Camera,
  Check,
  CircleAlert,
  Copy,
  Download,
  FileDown,
  ImagePlus,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  PartyPopper,
  Pencil,
  Printer,
  RefreshCw,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StorybookShell } from '@/components/storybook/StorybookShell';
import { StorybookPriceTag } from '@/components/storybook/StorybookPriceTag';
import { StorybookCover, StorybookPageText, StorybookReader } from '@/components/storybook/StorybookReader';
import { StorybookPrintTimeline } from '@/components/storybook/StorybookPrintTimeline';
import { StorybookPhotoTagger } from '@/components/storybook/StorybookPhotoTagger';
import { StorybookProof } from '@/components/storybook/StorybookProof';
import type { StorybookProofChoices } from '@/components/storybook/storybook-proof';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import {
  StorybookApiError,
  deleteStorybook,
  forgetStorybook,
  getStorybook,
  getStorybookConfig,
  getStorybookToken,
  proofApprove,
  proofRedraw,
  readStashedStorybookToken,
  redrawStorybook,
  rememberStorybook,
  retryStorybook,
  saveStorybookToken,
  stashStorybookCheckout,
  startStorybookCheckout,
  storybookMediaUrl,
  takeStorybookCheckout,
  type RedrawStorybookRequest,
  type StorybookBook as Book,
  type StorybookConfig,
  type StorybookPhotoSet,
  type StorybookPricePoint,
  type StorybookPrintStatus,
  type StorybookProduct,
  type StorybookProofRedrawRequest,
  type StorybookStatus,
} from '@/lib/storybook-api';
import { formatCountryList, formatStorybookPrice, storybookTotalWithShipping } from '@/lib/storybook-utils';
import { SpecPlate } from '@/components/trust/SpecPlate';
import { CheckoutStrip } from '@/components/trust/CheckoutStrip';
import { SUPPORT_EMAIL } from '@/lib/contact';
import { ShareCard } from '@/features/reel/ShareCard';
import {
  buildPhotoPayload,
  storybookTagLimits,
  type StorybookPhotoItem,
  type StorybookPhotoPayload,
  type StorybookTaggablePerson,
} from '@/lib/storybook-tagging';

// ---------------------------------------------------------------------------
// Polling. The site rate-limits every IP (300 requests / 15 min), so GET
// /books/:id is polled only while something is actually changing, and never
// while the tab is hidden:
//   every 4s  while the book is being written/illustrated/assembled, or the
//             print order is pending/submitting;
//   every 30s while the printer has it (submitted/accepted/in_production);
//   otherwise it stops. Proofing included: the book waits for the parent
//   there and only moves when they redraw or approve, which starts it again.
//   A print paid before approval sits at 'pending' all that time (nothing
//   acts on it until the approved book is ready, spec §9.1), so a proofing
//   book's print status never keeps the fast poll going.
// One bounded exception, from the spec: right after Stripe sends the parent
// back with ?checkout=success the book has not changed yet (fulfilment comes
// by webhook, OXXO cash can take hours), so it is re-checked every 10s for 3
// minutes, then every 60s, for at most 30 minutes. That covers a print
// upgrade bought during the check too, until the book says it is bought, and
// a book bought while its preview was still drawn whose preview finished
// before the payment landed (it waits at preview_ready, unpaid). A print
// upgrade bought while an early-paid book is still drawn is watched the same
// way, once this tab says the checkout was the print (a paid book being drawn
// says nothing about a print on its own: its PDF payment may simply have
// landed). While the watch waits for a payment, what it waits for is not
// offered again: no second checkout for the same thing.
// ---------------------------------------------------------------------------

const FAST_POLL_MS = 4000;
const SLOW_POLL_MS = 30000;
const ERROR_RETRY_MS = 15000;
const RATE_LIMITED_RETRY_MS = 60000;
const PAYMENT_FAST_MS = 10000;
const PAYMENT_SLOW_MS = 60000;
const PAYMENT_FAST_WINDOW_MS = 3 * 60 * 1000;
const PAYMENT_WATCH_MS = 30 * 60 * 1000;
const SLOW_PAYMENT_NOTE_MS = 60 * 1000;

const FAST_STATUSES: StorybookStatus[] = ['preview_generating', 'generating', 'assembling'];
const FAST_PRINT: StorybookPrintStatus[] = ['pending', 'submitting'];
const SLOW_PRINT: StorybookPrintStatus[] = ['submitted', 'accepted', 'in_production'];

type Awaiting = { kind: 'first' | 'print'; since: number };
type CheckoutOutcome = 'success' | 'cancelled';

/** A book a print can still be added to after it was paid for: in the parent's check, or ready. */
function printCanBeAdded(book: Book): boolean {
  return (book.status === 'proofing' || book.status === 'ready') && !book.print && !book.purchased?.print;
}

const isPaid = (book: Book) => Boolean(book.purchased?.pdf || book.purchased?.print);

/**
 * A print upgrade that has not reached the book yet: in the check or once
 * ready (as above), or on an early-paid book whose preview or pages are
 * still being drawn.
 */
function printUpgradePending(book: Book): boolean {
  if (printCanBeAdded(book)) return true;
  return FAST_STATUSES.includes(book.status) && isPaid(book) && !book.print && !book.purchased?.print;
}

/**
 * Deleting this book also cancels a print it has paid for: in the parent's
 * check nothing has gone to the printer yet (the server answers
 * print_in_progress if that changed meanwhile).
 */
function deletesUnsentPrint(book: Book): boolean {
  return book.status === 'proofing' && Boolean(book.purchased?.print) && (!book.print || book.print.status === 'pending');
}

/**
 * The banner after a successful checkout, from where the book was when the
 * parent came back (null: not loaded yet). Bought in the check: a print, made
 * after the approval. Bought once the pictures were approved (assembling,
 * ready): only a print is left to buy, and it goes to the printer.
 */
function checkoutSuccessKey(returnedTo: StorybookStatus | null): string {
  if (!returnedTo) return 'storybookPage.book.checkout.received';
  if (returnedTo === 'proofing') return 'storybookPage.book.checkout.successProof';
  if (returnedTo === 'assembling' || returnedTo === 'ready') return 'storybookPage.book.checkout.successPrint';
  return 'storybookPage.book.checkout.success';
}

/**
 * A first purchase that has not reached the book yet: on a finished preview,
 * or on one still being drawn (bought without waiting). A preview bought
 * while it was drawn stays preview_generating once paid, so that case is
 * decided by the purchase, not the status.
 */
function awaitingFirstPurchase(book: Book): boolean {
  return (book.status === 'preview_ready' || book.status === 'preview_generating') && !isPaid(book);
}

function awaitingPayment(book: Book, awaiting: Awaiting | null): boolean {
  if (!awaiting) return false;
  if (awaiting.kind === 'first') return awaitingFirstPurchase(book);
  return printUpgradePending(book);
}

function nextPollDelay(book: Book, awaiting: Awaiting | null): number | null {
  if (FAST_STATUSES.includes(book.status)) return FAST_POLL_MS;
  // A print paid before approval waits in proofing: nothing moves until the parent acts.
  const printStatus = book.status === 'proofing' ? undefined : book.print?.status;
  if (printStatus && FAST_PRINT.includes(printStatus)) return FAST_POLL_MS;
  if (printStatus && SLOW_PRINT.includes(printStatus)) return SLOW_POLL_MS;
  if (awaiting && awaitingPayment(book, awaiting)) {
    const elapsed = Date.now() - awaiting.since;
    if (elapsed < PAYMENT_FAST_WINDOW_MS) return PAYMENT_FAST_MS;
    if (elapsed < PAYMENT_WATCH_MS) return PAYMENT_SLOW_MS;
  }
  return null;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

const GENERATING_MESSAGES = ['m1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7', 'm8'] as const;
const KNOWN_ACTION_ERRORS = new Set([
  'already_purchased',
  'not_ready',
  'print_unavailable',
  'print_in_progress',
  'rate_limited',
  'network_error',
  'invalid_token',
  'missing_token',
  'not_found',
  // docs/storybook-identity-lock.md §2.5
  'not_retryable',
  'payments_unavailable',
  'checkout_failed',
  'create_failed',
  'database_unavailable',
  'server_error',
  'not_redrawable',
  'redraw_limit',
  'photos_too_large',
  'photo_face_too_small',
  'too_many_refs',
  'invalid_face_box',
  'photo_consent_required',
  'photo_too_large',
  'photo_too_small',
  'photo_unsupported',
  'photo_not_supported',
  // §9.3, the proof
  'not_in_proof',
  'proof_rounds_used',
  // DELETE: a payment reached the book between loading and deleting it
  'book_changed',
]);

function actionErrorKey(error: unknown): string {
  const code = error instanceof StorybookApiError ? error.code : 'generic';
  if (code === 'network_error') return 'storybookPage.errors.network';
  if (code === 'rate_limited' || code === 'daily_limit') return 'storybookPage.errors.busy';
  if (code === 'missing_token') return 'storybookPage.errors.invalid_token';
  return KNOWN_ACTION_ERRORS.has(code) ? `storybookPage.errors.${code}` : 'storybookPage.errors.generic';
}

/**
 * A proof action's answer: the whole book when the server sends one, else the
 * book we have, moved to the status the action leads to (the poll that
 * follows fills in the rest). A redraw remembers which pictures it asked for.
 */
function afterProofAction(
  current: Book | null,
  answer: Partial<Book> | null | undefined,
  status: StorybookStatus,
  redrawing?: string[],
): Book | null {
  const reply = answer && typeof answer === 'object' ? answer : null;
  const next: Book | null =
    reply && typeof reply.id === 'string' && reply.status
      ? (reply as Book)
      : current
        ? { ...current, status: reply?.status || status, error: null }
        : null;
  if (!next || !redrawing?.length || next.status !== 'generating' || next.proof?.redrawing?.length) return next;
  return { ...next, proof: { ...(next.proof || {}), redrawing } };
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function StateCard({ emoji, title, children, role }: { emoji: string; title: string; children?: ReactNode; role?: 'alert' | 'status' }) {
  return (
    <div role={role} className="glass-card mx-auto max-w-xl p-8 text-center sm:p-10">
      <p className="text-5xl" aria-hidden="true">{emoji}</p>
      <h1 className="mt-4 text-2xl font-semibold sm:text-3xl">{title}</h1>
      {children}
    </div>
  );
}

function IndeterminateBar({ label }: { label: string }) {
  return (
    <div role="progressbar" aria-label={label} className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
      <div className="storybook-indeterminate absolute inset-y-0 left-0 w-2/5 rounded-full bg-[linear-gradient(90deg,hsl(var(--primary)),hsl(330_68%_52%),hsl(45_95%_55%))]" />
    </div>
  );
}

function PrivateLinkCard({ id, token, emphasize }: { id: string; token: string; emphasize?: boolean }) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/storybook/${encodeURIComponent(id)}?k=${encodeURIComponent(token)}`;

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  // Never tracked: the link IS the key to the book.
  const copy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      input.select();
      input.setSelectionRange(0, link.length);
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success(t('storybookPage.book.privateLink.copied'));
    } else {
      toast.error(t('storybookPage.book.privateLink.copyFailed'));
    }
  };

  return (
    <section
      aria-labelledby="sb-private-link-title"
      data-testid="storybook-private-link"
      className={cn(
        'ph-no-capture rounded-3xl border p-5 sm:p-6',
        emphasize ? 'border-primary bg-card shadow-[0_0_0_1px_hsl(var(--primary)),var(--shadow-card)]' : 'border-border bg-card shadow-[var(--shadow-card)]',
      )}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
          <KeyRound className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 id="sb-private-link-title" className="text-lg font-semibold">{t('storybookPage.book.privateLink.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('storybookPage.book.privateLink.body')}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <label htmlFor="sb-private-link" className="sr-only">
          {t('storybookPage.book.privateLink.label')}
        </label>
        <Input
          id="sb-private-link"
          ref={inputRef}
          readOnly
          value={link}
          onFocus={(event) => event.currentTarget.select()}
          className="h-11 min-w-0 flex-1 font-mono text-xs md:text-xs"
        />
        <Button type="button" onClick={() => void copy()} className="h-11 shrink-0 rounded-full">
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? t('storybookPage.book.privateLink.copiedShort') : t('storybookPage.book.privateLink.copy')}
        </Button>
      </div>
      <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('storybookPage.book.privateLink.warning')}
      </p>
    </section>
  );
}

/** Shipping charged on top of a printed book (GET /config); null = free shipping. */
type PaidShipping = StorybookPricePoint | null;

interface PurchasePanelProps {
  book: Book;
  countries: string;
  shipping: PaidShipping;
  buying: StorybookProduct | null;
  onBuy: (product: StorybookProduct) => void;
  title: string;
  subtitle: string;
  /** Back from Stripe, the payment not on the book yet: "Payment received" in place of the offers. */
  paymentLanding?: boolean;
}

function PurchasePanel({ book, countries, shipping, buying, onBuy, title, subtitle, paymentLanding = false }: PurchasePanelProps) {
  const { t } = useTranslation();
  const pdf = book.offers?.pdf;
  const print = book.offers?.print;
  const showPdf = Boolean(pdf?.available);
  const showPrint = Boolean(print?.available);
  if (!showPdf && !showPrint) return null;
  if (paymentLanding) return <PaymentLandingCard />;
  const upgrade = print?.kind === 'upgrade';
  const shippingText = shipping ? formatStorybookPrice(shipping) : null;
  // The book (or upgrade) as this checkout charges it, plus Stripe's shipping line.
  const printTotal = print ? storybookTotalWithShipping(print, shipping) : null;

  const offerButton = (product: StorybookProduct, label: string, Icon: typeof FileDown, primary: boolean) => (
    <Button
      type="button"
      size="lg"
      variant={primary ? 'default' : 'outline'}
      className="mt-4 w-full rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug"
      disabled={buying !== null}
      onClick={() => onBuy(product)}
      data-testid={`buy-${product}`}
    >
      {buying === product ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Icon aria-hidden="true" />}
      {buying === product ? t('storybookPage.book.purchase.redirecting') : label}
    </Button>
  );

  return (
    <section
      aria-labelledby="sb-purchase-title"
      data-testid="storybook-purchase"
      className="rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6"
    >
      <h2 id="sb-purchase-title" className="text-2xl font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      <div className="mt-5 space-y-4">
        {showPdf && pdf && (
          <div className="rounded-2xl border border-border p-4" data-testid="offer-pdf">
            <p className="flex items-center gap-2 font-semibold">
              <FileDown className="h-5 w-5 text-primary" aria-hidden="true" />
              {t('storybookPage.pricing.pdf.name')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{t('storybookPage.book.purchase.pdfDesc')}</p>
            <StorybookPriceTag size="md" className="mt-3" cents={pdf.cents} usdCents={pdf.usdCents} />
            {offerButton('pdf', t('storybookPage.book.purchase.buyPdf'), Download, !showPrint)}
          </div>
        )}
        {showPrint && print && (
          <div className="relative rounded-2xl border border-primary bg-primary/5 p-4" data-testid="offer-print">
            {upgrade && (
              <span className="absolute -top-2.5 right-4 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-accent-foreground">
                {t('storybookPage.book.purchase.upgradeBadge')}
              </span>
            )}
            <p className="flex items-center gap-2 font-semibold">
              <Printer className="h-5 w-5 text-primary" aria-hidden="true" />
              {t('storybookPage.pricing.print.name')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {upgrade
                ? t('storybookPage.book.purchase.upgradeNote')
                : t(shipping ? 'storybookPage.book.purchase.printDescPaid' : 'storybookPage.book.purchase.printDesc')}
            </p>
            <StorybookPriceTag size="md" className="mt-3" cents={print.cents} usdCents={print.usdCents} />
            {printTotal && (
              <p className="mt-2 text-sm font-semibold" data-testid="offer-print-total">
                {t('storybookPage.pricing.print.totalWithShipping', printTotal)}
              </p>
            )}
            {offerButton(
              'print',
              upgrade ? t('storybookPage.book.purchase.buyUpgrade') : t('storybookPage.book.purchase.buyPrint'),
              BookHeart,
              true,
            )}
          </div>
        )}
      </div>
      {showPrint && countries && (
        <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
          <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {shippingText
            ? t('storybookPage.book.purchase.shipsToPaid', { countries, ...shippingText })
            : t('storybookPage.book.purchase.shipsTo', { countries })}
        </p>
      )}
      <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('storybookPage.book.purchase.secure')}
      </p>
      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{t('storybookPage.pricing.totalAtCheckout')}</p>
      <CheckoutStrip methods="storybook" />
    </section>
  );
}

/**
 * In place of the offers while a payment the parent just made is on its way
 * (a slow webhook, cash at a store): no second checkout for the same thing.
 * The offers come back once it lands, or when the payment watch gives up.
 */
function PaymentLandingCard() {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="sb-payment-landing-title"
      data-testid="storybook-payment-landing"
      className="rounded-3xl border border-emerald-600/30 bg-emerald-50 p-5 text-center text-emerald-950 shadow-[var(--shadow-card)] sm:p-6"
    >
      <PartyPopper className="mx-auto h-6 w-6 text-emerald-700" aria-hidden="true" />
      <h2 id="sb-payment-landing-title" className="mt-2 text-xl font-semibold">{t('storybookPage.book.checkout.received')}</h2>
      <p className="mt-1 inline-flex items-center justify-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        {t('storybookPage.book.checkout.confirming')}
      </p>
    </section>
  );
}

/** How the cover drew each person (order.likeness), so the parent knows what a photo did. */
function LikenessCard({ book }: { book: Book }) {
  const { t } = useTranslation();
  const likeness = book.likeness;
  if (!likeness) return null;
  const cast = book.cast?.length ? book.cast : [{ id: 'hero' as const, name: book.childName }];
  const rows = cast.filter((member) => likeness[member.id]);
  if (!rows.length) return null;
  return (
    <section
      aria-labelledby="sb-likeness-title"
      data-testid="storybook-likeness"
      className="ph-no-capture rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6"
    >
      <h2 id="sb-likeness-title" className="text-lg font-semibold">
        {t('storybookPage.book.likeness.title')}
      </h2>
      <ul className="mt-3 space-y-2 text-sm">
        {rows.map((member) => {
          const state = likeness[member.id];
          const name = member.name || (member.id === 'hero' ? book.childName : '');
          return (
            <li key={member.id} className={cn('flex items-start gap-2', state === 'photo_unreadable' && 'font-medium text-destructive')}>
              {state === 'photo' ? (
                <Camera className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
              ) : state === 'photo_unreadable' ? (
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <span className="min-w-0 break-words">
                {state === 'photo'
                  ? t('storybookPage.book.likeness.photo', { name })
                  : state === 'photo_unreadable'
                    ? t('storybookPage.book.likeness.unreadable', { name })
                    : t('storybookPage.book.likeness.description', { name })}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface RedrawPanelProps {
  book: Book;
  canUpdatePhotos: boolean;
  busy: boolean;
  onRedraw: () => void;
  onUpdatePhotos: () => void;
}

/** "Not quite them?": redraw the cover, or replace the photos first (order.redraw, before paying). */
function RedrawPanel({ book, canUpdatePhotos, busy, onRedraw, onUpdatePhotos }: RedrawPanelProps) {
  const { t } = useTranslation();
  if (!book.redraw?.allowed) return null;
  const left = Math.max(0, book.redraw.left ?? 0);
  return (
    <section
      aria-labelledby="sb-redraw-title"
      data-testid="storybook-redraw"
      className="rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6"
    >
      <h2 id="sb-redraw-title" className="text-lg font-semibold">
        {t('storybookPage.book.redraw.title')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t(canUpdatePhotos ? 'storybookPage.book.redraw.body' : 'storybookPage.book.redraw.bodyNoPhotos')}
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          type="button"
          variant="outline"
          className="h-auto min-h-11 whitespace-normal rounded-full bg-card py-2.5 text-center leading-snug"
          onClick={onRedraw}
          disabled={busy || left <= 0}
          data-testid="storybook-redraw-cover"
        >
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
          {t('storybookPage.book.redraw.button', { count: left })}
        </Button>
        {canUpdatePhotos && (
          <Button
            type="button"
            variant="outline"
            className="h-auto min-h-11 whitespace-normal rounded-full bg-card py-2.5 text-center leading-snug"
            onClick={onUpdatePhotos}
            disabled={busy || left <= 0}
          >
            <ImagePlus aria-hidden="true" />
            {t('storybookPage.book.redraw.updatePhotos')}
          </Button>
        )}
      </div>
    </section>
  );
}

interface UpdatePhotosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  book: Book;
  config: StorybookConfig | null;
  /** Sends the photos (POST /redraw); resolves with the error, or null once the redraw started. */
  onSubmit: (body: StorybookPhotoSet) => Promise<StorybookApiError | null>;
}

/** 'photos[1]' / 'faces[0]' from a failed redraw -> the photo it points at. */
function photoFromField(field: string | undefined, sent: StorybookPhotoPayload['sent']): string | null {
  const match = field ? /^(photos|faces)\[(\d+)\]/.exec(field) : null;
  if (!match) return null;
  const index = Number(match[2]);
  return match[1] === 'photos' ? sent.photos[index] ?? null : sent.faces[index]?.photo ?? null;
}

/**
 * 'appearance.skinTone' / 'companions[0].appearance.hairColor' from a failed
 * redraw: someone tagged whose colour chips were never chosen for this book,
 * so a photo of them can't be used (docs/storybook-identity-lock.md §12) ->
 * their cast id.
 */
function uncolouredPerson(field: string | undefined): string | null {
  const match = field ? /^(?:companions\[(\d+)\]\.|(companion)\.)?appearance\.(?:skinTone|hairColor)$/.exec(field) : null;
  if (!match) return null;
  if (match[1] !== undefined) return `c${Number(match[1]) + 1}`;
  return match[2] ? 'c1' : 'hero';
}

/**
 * "Update photos": the builder's photo step again, for the book's own cast.
 * Only the people tagged here get new photos (the others keep theirs), then
 * the cover is drawn again. The same separate photo consent is required.
 */
function UpdatePhotosDialog({ open, onOpenChange, book, config, onSubmit }: UpdatePhotosDialogProps) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<StorybookPhotoItem[]>([]);
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoErrors, setPhotoErrors] = useState<Record<string, string>>({});
  const limits = useMemo(() => storybookTagLimits(config?.limits), [config]);

  const people = useMemo<StorybookTaggablePerson[]>(() => {
    const photoRelations = config?.photoRelations || [];
    const cast = book.cast?.length ? book.cast : [{ id: 'hero' as const, name: book.childName }];
    return cast.map((member) => ({
      key: member.id,
      id: member.id,
      name: member.name || (member.id === 'hero' ? book.childName : member.id),
      // The child always; a companion by relation (never a friend).
      photoAllowed: member.id === 'hero' || photoRelations.includes((member as { relation?: string }).relation || ''),
    }));
  }, [book.cast, book.childName, config]);

  // Whoever the server already draws from a photo keeps it unless tagged here
  // (§2.4). Not a photo it couldn't read: that one should be replaced.
  const keptPhotos = useMemo(
    () =>
      new Set(
        book.photosKept ? people.filter((person) => person.photoAllowed && book.likeness?.[person.id] === 'photo').map((person) => person.key) : [],
      ),
    [book.photosKept, book.likeness, people],
  );

  // Every opening starts empty: nothing from an earlier try is sent by mistake.
  useEffect(() => {
    if (!open) return;
    setPhotos([]);
    setConsent(false);
    setConsentError(false);
    setError(null);
    setPhotoErrors({});
  }, [open]);

  const payload = buildPhotoPayload(photos, people, limits);
  const ready = payload.photos.length > 0;

  const submit = async () => {
    if (sending || busy) return;
    if (!ready) {
      setError(t('storybookPage.book.updatePhotos.needFace'));
      return;
    }
    if (!consent) {
      setConsentError(true);
      return;
    }
    setError(null);
    setSending(true);
    const failure = await onSubmit({
      photos: payload.photos,
      faces: payload.faces,
      photoConsent: true,
      ...(config?.consentVersion ? { consentVersion: config.consentVersion } : {}),
    });
    setSending(false);
    if (!failure) return;
    const uncoloured = failure.code === 'invalid_input' ? people.find((person) => person.id === uncolouredPerson(failure.field)) : undefined;
    const message = uncoloured ? t('storybookPage.book.updatePhotos.coloursMissing', { name: uncoloured.name }) : t(actionErrorKey(failure));
    setError(message);
    const photoKey = photoFromField(failure.field, payload.sent);
    if (photoKey) setPhotoErrors({ [photoKey]: message });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !sending && onOpenChange(next)}>
      {/* Portaled outside the page's ph-no-capture container: it needs its own. */}
      <DialogContent className="ph-no-capture storybook-theme max-h-[92dvh] w-[calc(100%-1rem)] max-w-2xl overflow-y-auto rounded-2xl bg-background p-4 text-foreground sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('storybookPage.book.updatePhotos.title')}</DialogTitle>
          <DialogDescription>{t('storybookPage.book.updatePhotos.body')}</DialogDescription>
        </DialogHeader>
        <StorybookPhotoTagger
          idPrefix="sb-update-photos"
          people={people}
          keptPhotos={keptPhotos}
          photos={photos}
          onPhotosChange={setPhotos}
          limits={limits}
          onBusyChange={setBusy}
          photoErrors={photoErrors}
          onPhotoEdited={() => {
            setPhotoErrors({});
            setError(null);
          }}
        />
        {ready && (
          <div className="space-y-2">
            <div className={cn('flex items-start gap-3 rounded-2xl border bg-card p-4', consentError ? 'border-destructive' : 'border-border')}>
              <Checkbox
                id="sb-update-photo-consent"
                checked={consent}
                onCheckedChange={(checked) => {
                  setConsent(checked === true);
                  setConsentError(false);
                }}
                aria-invalid={consentError || undefined}
                aria-describedby={consentError ? 'sb-update-photo-consent-error' : undefined}
                className="mt-0.5 h-5 w-5"
              />
              <Label htmlFor="sb-update-photo-consent" className="cursor-pointer text-sm font-normal leading-relaxed">
                {t('storybookPage.fields.photoConsent.label')}
              </Label>
            </div>
            {consentError && (
              <p id="sb-update-photo-consent-error" className="text-sm font-medium text-destructive">
                {t('storybookPage.validation.photoConsentRequired')}
              </p>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={sending}>
            {t('storybookPage.book.updatePhotos.cancel')}
          </Button>
          <Button type="button" className="rounded-full" onClick={() => void submit()} disabled={sending || busy}>
            {sending ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
            {sending ? t('storybookPage.book.updatePhotos.submitting') : t('storybookPage.book.updatePhotos.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneratingView() {
  const { t } = useTranslation();
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setMessageIndex((i) => (i + 1) % GENERATING_MESSAGES.length), 3500);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="glass-card mx-auto max-w-xl p-8 text-center sm:p-10" data-testid="storybook-generating">
      <p className="storybook-bob inline-block text-6xl" aria-hidden="true">📖</p>
      <h1 className="mt-4 text-2xl font-semibold sm:text-3xl">{t('storybookPage.book.generating.title')}</h1>
      <p className="mt-2 min-h-[1.5rem] text-base font-medium text-primary" aria-hidden="true">
        {t(`storybookPage.book.generating.messages.${GENERATING_MESSAGES[messageIndex]}`)}
      </p>
      <div className="mt-6">
        <IndeterminateBar label={t('storybookPage.book.generating.title')} />
      </div>
      <p className="mt-5 text-sm text-muted-foreground">{t('storybookPage.book.generating.subtitle')}</p>
      <p className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Mail className="h-3.5 w-3.5" aria-hidden="true" />
        {t('storybookPage.book.generating.emailNote')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function StorybookBook() {
  const { id = '' } = useParams<{ id: string }>();
  // Keyed by id so moving between books starts from a clean slate.
  return <StorybookBookView key={id} id={id} />;
}

function StorybookBookView({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read once: the address bar is cleaned below.
  const [entry] = useState(() => {
    const checkout = searchParams.get('checkout');
    return {
      k: searchParams.get('k'),
      checkout: (checkout === 'success' || checkout === 'cancelled' ? checkout : null) as CheckoutOutcome | null,
    };
  });
  const [token] = useState<string | null>(() => entry.k || readStashedStorybookToken(id) || getStorybookToken(id));
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [loadError, setLoadError] = useState<StorybookApiError | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleted, setDeleted] = useState(false);
  const [banner, setBanner] = useState<CheckoutOutcome | null>(entry.checkout);
  // Where the book was when the parent came back from a successful checkout
  // (null until it loads): back to the check means a print upgrade, which
  // waits for the approval.
  const [returnedTo, setReturnedTo] = useState<StorybookStatus | null>(null);
  const [slowPayment, setSlowPayment] = useState(false);
  const [buying, setBuying] = useState<StorybookProduct | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [printCountries, setPrintCountries] = useState<string[]>([]);
  const [shipping, setShipping] = useState<PaidShipping>(null);
  const [config, setConfig] = useState<StorybookConfig | null>(null);
  const [redrawing, setRedrawing] = useState(false);
  const [updatingPhotos, setUpdatingPhotos] = useState(false);
  // The parent's answer per picture of the proof. Kept here, not in the proof
  // view, so the pictures they already passed stay passed through a redraw of
  // other pages. Not through a cover redraw: the pages copy the cover's faces
  // for people without photos, so every picture needs a fresh look then.
  const [proofChoices, setProofChoices] = useState<StorybookProofChoices>({});
  const [proofBusy, setProofBusy] = useState<'redraw' | 'approve' | null>(null);

  const awaitingRef = useRef<Awaiting | null>(null);
  const awaitingDecided = useRef(entry.checkout !== 'success');
  // What this tab paid for (stashed by buy() before Stripe), read once on the way back.
  const checkoutProduct = useRef<StorybookProduct | null>(null);
  // True from the moment a payment is watched for until the watch gives up.
  const [paymentWatch, setPaymentWatch] = useState(false);
  const prevStatus = useRef<StorybookStatus | null>(null);
  const handledEntry = useRef(false);
  const lastMediaRefresh = useRef(0);

  // 1. The email link's ?k= is saved and every one-time param leaves the
  //    address bar (history.replaceState via the router).
  useEffect(() => {
    if (handledEntry.current) return;
    handledEntry.current = true;
    if (entry.k) saveStorybookToken(id, entry.k);
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const key of ['k', 'checkout', 'session_id']) {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    }
    if (changed) setSearchParams(next, { replace: true });
    if (entry.checkout) {
      const bought = takeStorybookCheckout(id);
      if (entry.checkout === 'success') checkoutProduct.current = bought;
      trackEvent('storybook_checkout_returned', { outcome: entry.checkout });
      // Before the book has loaded: nothing here can say yet what happens next (the banner does).
      if (entry.checkout === 'success') toast.success(t('storybookPage.book.checkout.received'));
    }
  }, [entry, id, searchParams, setSearchParams, t]);

  // 2. After a successful checkout, mention a cash payment's delay if nothing moved in a minute.
  useEffect(() => {
    if (entry.checkout !== 'success') return;
    const timer = window.setTimeout(() => setSlowPayment(true), SLOW_PAYMENT_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [entry.checkout]);

  //    The payment watch gives up after 30 minutes: whatever it waited for is offered again.
  useEffect(() => {
    if (!paymentWatch) return;
    const timer = window.setTimeout(() => setPaymentWatch(false), PAYMENT_WATCH_MS);
    return () => window.clearTimeout(timer);
  }, [paymentWatch]);

  // 3. Print countries for the "ships to" note (the book response has none),
  //    and the photo limits for "Update photos".
  useEffect(() => {
    const controller = new AbortController();
    getStorybookConfig(controller.signal)
      .then((config) => {
        setConfig(config);
        setPrintCountries(Array.isArray(config.printCountries) ? config.printCountries : []);
        const ship = config.prices?.shipping;
        setShipping(ship && ship.cents > 0 ? { cents: ship.cents, usdCents: ship.usdCents } : null);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  // 4. Load + poll the book.
  useEffect(() => {
    if (!token || deleted) return;
    let cancelled = false;
    let timer: number | undefined;
    let controller: AbortController | null = null;
    let waitingForVisible = false;

    const receive = (next: Book) => {
      setBook(next);
      setLoadError(null);
      if (!awaitingDecided.current) {
        awaitingDecided.current = true;
        setReturnedTo(next.status);
        // Bought while the preview was drawn: if the preview finishes before
        // the payment lands (OXXO cash, a slow webhook), the book waits at
        // preview_ready and is still watched for the payment there. A print
        // upgrade bought while an early-paid book is drawn is watched when
        // this tab says the checkout was the print.
        if (awaitingFirstPurchase(next)) awaitingRef.current = { kind: 'first', since: Date.now() };
        else if (printCanBeAdded(next) || (checkoutProduct.current === 'print' && printUpgradePending(next))) {
          awaitingRef.current = { kind: 'print', since: Date.now() };
        }
        if (awaitingRef.current) setPaymentWatch(true);
      }
      if (prevStatus.current === 'preview_generating' && next.status === 'preview_ready') {
        trackEvent('storybook_preview_ready');
      }
      if (prevStatus.current === 'generating' && next.status === 'proofing') {
        trackEvent('storybook_proof_ready', { rounds_left: next.proof?.roundsLeft });
      }
      prevStatus.current = next.status;
      if (next.status === 'deleted') forgetStorybook(id);
      else rememberStorybook(id, next.createdAt || new Date().toISOString());
    };

    const schedule = (delay: number | null) => {
      if (cancelled || delay == null) return;
      timer = window.setTimeout(tick, delay);
    };

    function tick() {
      timer = undefined;
      if (typeof document !== 'undefined' && document.hidden) {
        waitingForVisible = true;
        return;
      }
      void load();
    }

    async function load() {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      try {
        const next = await getStorybook(id, token as string, current.signal);
        if (cancelled) return;
        receive(next);
        schedule(nextPollDelay(next, awaitingRef.current));
      } catch (error) {
        if (cancelled || isAbortError(error)) return;
        const apiError = error instanceof StorybookApiError ? error : new StorybookApiError(0, 'network_error');
        setLoadError(apiError);
        if (apiError.status === 404) forgetStorybook(id);
        if (apiError.status === 429) schedule(RATE_LIMITED_RETRY_MS);
        else if (apiError.status === 0 || apiError.status >= 500) schedule(ERROR_RETRY_MS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    const onVisibility = () => {
      if (!document.hidden && waitingForVisible) {
        waitingForVisible = false;
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    void load();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [id, token, deleted, refreshKey]);

  const refresh = () => setRefreshKey((key) => key + 1);

  // Signed media URLs expire; a broken image fetches fresh ones (at most once a minute).
  const onMediaError = () => {
    const now = Date.now();
    if (now - lastMediaRefresh.current < 60000) return;
    lastMediaRefresh.current = now;
    refresh();
  };

  const buy = async (product: StorybookProduct) => {
    if (!book || !token || buying) return;
    const offer = product === 'pdf' ? book.offers?.pdf : book.offers?.print;
    if (!offer?.available) return;
    setBuying(product);
    try {
      const { url } = await startStorybookCheckout(book.id || id, token, product);
      if (!url) throw new StorybookApiError(502, 'generic');
      trackEvent('checkout_started', {
        checkout_flow:
          product === 'pdf'
            ? 'storybook_pdf'
            : book.offers?.print?.kind === 'upgrade'
              ? 'storybook_print_upgrade'
              : 'storybook_print',
        currency: 'MXN',
        value_mxn: offer.cents / 100,
        // Bought without waiting for the free preview.
        before_preview: book.status === 'preview_generating',
      });
      stashStorybookCheckout(id, product);
      window.location.href = url;
    } catch (error) {
      setBuying(null);
      const code = error instanceof StorybookApiError ? error.code : 'generic';
      if (code === 'already_purchased') {
        toast.info(t('storybookPage.errors.already_purchased'));
        refresh();
        return;
      }
      toast.error(t(actionErrorKey(error)));
    }
  };

  const retry = async () => {
    if (!token || retrying) return;
    setRetrying(true);
    try {
      const result = await retryStorybook(id, token);
      setBook((current) =>
        current ? { ...current, status: result?.status || 'preview_generating', error: null, retryable: false } : current,
      );
      prevStatus.current = result?.status || 'preview_generating';
      refresh();
    } catch (error) {
      toast.error(t(actionErrorKey(error)));
    } finally {
      setRetrying(false);
    }
  };

  /**
   * POST /redraw. The answer is the book, now preview_generating, and polling
   * starts again from it. Resolves with the error instead of throwing.
   */
  const redraw = async (body: RedrawStorybookRequest = {}): Promise<StorybookApiError | null> => {
    if (!token || redrawing) return null;
    setRedrawing(true);
    const photoCount = 'photos' in body && Array.isArray(body.photos) ? body.photos.length : 0;
    try {
      const next = await redrawStorybook(id, token, body);
      // Counts only: never who is in a photo.
      trackEvent('storybook_cover_redraw_requested', { with_photos: photoCount > 0, photo_count: photoCount });
      setBook((current) =>
        next && typeof next === 'object' && next.status
          ? next
          : current
            ? { ...current, status: 'preview_generating', error: null, retryable: false }
            : current,
      );
      prevStatus.current = 'preview_generating';
      refresh();
      return null;
    } catch (error) {
      const apiError = error instanceof StorybookApiError ? error : new StorybookApiError(0, 'network_error');
      // The server's own count moved on (another tab, the limit): show what it says now.
      if (apiError.code === 'not_redrawable' || apiError.code === 'redraw_limit') refresh();
      return apiError;
    } finally {
      setRedrawing(false);
    }
  };

  const redrawCover = async () => {
    const failure = await redraw({});
    if (failure) toast.error(t(actionErrorKey(failure)));
  };

  const submitNewPhotos = async (body: StorybookPhotoSet) => {
    const failure = await redraw(body);
    if (!failure) setUpdatingPhotos(false);
    return failure;
  };

  /** A refused proof action. The book moved on (another tab, the last round went): show where it is now. */
  const proofFailed = (error: unknown) => {
    const apiError = error instanceof StorybookApiError ? error : new StorybookApiError(0, 'network_error');
    if (apiError.code === 'not_in_proof' || apiError.code === 'proof_rounds_used') refresh();
    toast.error(t(apiError.code === 'invalid_input' ? 'storybookPage.book.proof.invalidInput' : actionErrorKey(apiError)));
  };

  /** POST /proof/redraw: the marked pictures are drawn again, and polling starts again. */
  const sendProofRedraw = async (request: StorybookProofRedrawRequest): Promise<boolean> => {
    if (!token || proofBusy || !request.keys.length) return false;
    setProofBusy('redraw');
    try {
      const answer = await proofRedraw(id, token, request);
      const reasons = Object.values(request.reasons);
      // Counts only: never a picture, a page's words or who is in it.
      trackEvent('storybook_proof_redraw_requested', {
        picture_count: request.keys.length,
        includes_cover: request.keys.includes('cover'),
        reason_face: reasons.filter((reason) => reason === 'face').length,
        reason_wrong_person: reasons.filter((reason) => reason === 'wrong_person').length,
        reason_other: reasons.filter((reason) => reason === 'other').length,
      });
      setBook((current) => afterProofAction(current, answer, 'generating', request.keys));
      prevStatus.current = answer?.status || 'generating';
      // A redrawn picture is new and needs a fresh look; the others keep their answer.
      // What is redrawn is what the server says (it can add pages to a round),
      // and always what was asked. A new cover changes the faces the pages copy
      // for people without photos (spec §10): then every picture needs a fresh look.
      const listed = answer?.proof?.redrawing;
      const redrawn = new Set([...(Array.isArray(listed) ? listed : []), ...request.keys]);
      setProofChoices((current) =>
        redrawn.has('cover') ? {} : Object.fromEntries(Object.entries(current).filter(([key]) => !redrawn.has(key))),
      );
      refresh();
      return true;
    } catch (error) {
      proofFailed(error);
      return false;
    } finally {
      setProofBusy(null);
    }
  };

  /** POST /proof/approve: only now is the book made (and printed). */
  const approveProof = async (): Promise<boolean> => {
    if (!token || proofBusy) return false;
    const proof = book?.proof;
    setProofBusy('approve');
    try {
      const answer = await proofApprove(id, token);
      trackEvent('storybook_proof_approved', {
        rounds_used:
          typeof proof?.maxRounds === 'number' && typeof proof?.roundsLeft === 'number'
            ? Math.max(0, proof.maxRounds - proof.roundsLeft)
            : undefined,
      });
      setBook((current) => afterProofAction(current, answer, 'assembling'));
      prevStatus.current = answer?.status || 'assembling';
      setProofChoices({});
      refresh();
      return true;
    } catch (error) {
      proofFailed(error);
      return false;
    } finally {
      setProofBusy(null);
    }
  };

  const remove = async () => {
    if (!token || deleting) return;
    setDeleting(true);
    try {
      await deleteStorybook(id, token);
      forgetStorybook(id);
      setConfirmDelete(false);
      setDeleted(true);
      toast.success(t('storybookPage.book.deleteDialog.done'));
    } catch (error) {
      const code = error instanceof StorybookApiError ? error.code : '';
      if (code === 'not_found') {
        forgetStorybook(id);
        setConfirmDelete(false);
        setDeleted(true);
      } else {
        toast.error(t(actionErrorKey(error)));
        // Nothing was deleted because the book moved on (a payment landed, the
        // print went to the printer): show it as it is now.
        if (code === 'book_changed' || code === 'print_in_progress') {
          setConfirmDelete(false);
          refresh();
        }
      }
    } finally {
      setDeleting(false);
    }
  };

  const countries = formatCountryList(printCountries, i18n.language);
  const purchased = Boolean(book?.purchased?.pdf || book?.purchased?.print);
  const photosEnabled = (config?.limits?.maxPhotos ?? 0) > 0;
  // Back from Stripe before the payment reached the book: until it does (or
  // the watch gives up), what was just paid for is not offered again.
  const paymentLanding = Boolean(book && paymentWatch && awaitingPayment(book, awaitingRef.current));

  const redrawPanel = book ? (
    <RedrawPanel
      book={book}
      canUpdatePhotos={photosEnabled}
      busy={redrawing}
      onRedraw={() => void redrawCover()}
      onUpdatePhotos={() => setUpdatingPhotos(true)}
    />
  ) : null;

  // -------------------------------------------------------------------------
  // Views
  // -------------------------------------------------------------------------

  const newStoryLink = (
    <Button asChild size="lg" className="mt-6 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug">
      <Link to="/storybook#builder">
        <BookHeart aria-hidden="true" />
        {t('storybookPage.book.actions.newStory')}
      </Link>
    </Button>
  );

  const deleteButton = (label: string) => (
    <Button
      type="button"
      variant="ghost"
      className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
      onClick={() => setConfirmDelete(true)}
    >
      <Trash2 aria-hidden="true" />
      {label}
    </Button>
  );

  const secondaryActions = (label: string) => (
    <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
      <Button asChild variant="outline" className="rounded-full bg-card">
        <Link to="/storybook#builder">
          <BookHeart aria-hidden="true" />
          {t('storybookPage.book.actions.newStory')}
        </Link>
      </Button>
      {deleteButton(label)}
    </div>
  );

  let content: ReactNode;

  if (deleted || book?.status === 'deleted') {
    content = (
      <StateCard emoji="🧹" title={t('storybookPage.book.deleted.title')} role="status">
        <p className="mt-3 text-muted-foreground">{t('storybookPage.book.deleted.body')}</p>
        {newStoryLink}
      </StateCard>
    );
  } else if (!token) {
    content = (
      <StateCard emoji="💌" title={t('storybookPage.book.noToken.title')}>
        <p className="mt-3 text-muted-foreground">{t('storybookPage.book.noToken.body')}</p>
        {newStoryLink}
      </StateCard>
    );
  } else if (!book && loading) {
    content = (
      <div role="status" className="flex items-center justify-center gap-3 py-24 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        {t('storybookPage.book.loading')}
      </div>
    );
  } else if (!book) {
    const code = loadError?.code;
    const denied = code === 'invalid_token' || code === 'missing_token';
    const missing = code === 'not_found';
    content = (
      <StateCard
        emoji={denied ? '🔐' : missing ? '🔍' : '🙈'}
        title={
          denied
            ? t('storybookPage.book.errors.deniedTitle')
            : missing
              ? t('storybookPage.book.errors.notFoundTitle')
              : t('storybookPage.book.errors.loadTitle')
        }
        role="alert"
      >
        <p className="mt-3 text-muted-foreground">
          {denied
            ? t('storybookPage.book.errors.deniedBody')
            : missing
              ? t('storybookPage.book.errors.notFoundBody')
              : t('storybookPage.book.errors.loadBody')}
        </p>
        {!denied && !missing ? (
          <Button variant="outline" size="lg" className="mt-6 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug" onClick={refresh}>
            <RefreshCw aria-hidden="true" />
            {t('storybookPage.builder.retry')}
          </Button>
        ) : (
          newStoryLink
        )}
      </StateCard>
    );
  } else if (book.status === 'preview_generating') {
    // No need to wait for the preview: the book can be bought while it is
    // drawn (the pages follow the cover, and the parent still checks every
    // picture before anything is made). Once bought it keeps drawing here,
    // and only the print upgrade is left, as while the pages are painted.
    const canBuy = Boolean(book.offers?.pdf?.available || book.offers?.print?.available);
    content = (
      <div className="space-y-8">
        <GeneratingView />
        {purchased && <PrivateLinkCard id={id} token={token} emphasize />}
        {canBuy && (
          <div className="mx-auto max-w-xl" data-testid="storybook-buy-early">
            <PurchasePanel
              book={book}
              countries={countries}
              shipping={shipping}
              buying={buying}
              onBuy={(product) => void buy(product)}
              paymentLanding={paymentLanding}
              title={purchased ? t('storybookPage.book.upsell.title') : t('storybookPage.book.purchase.earlyTitle')}
              subtitle={
                purchased
                  ? t(shipping ? 'storybookPage.book.upsell.bodyPaid' : 'storybookPage.book.upsell.body')
                  : t('storybookPage.book.purchase.earlyBody')
              }
            />
          </div>
        )}
      </div>
    );
  } else if (book.status === 'preview_failed' || book.status === 'failed') {
    const paid = book.status === 'failed' && purchased;
    content = (
      <div className="space-y-6">
        <StateCard
          emoji="🫣"
          title={paid ? t('storybookPage.book.failed.bookTitle') : t('storybookPage.book.failed.previewTitle')}
          role="alert"
        >
          <p className="mt-3 text-muted-foreground">
            {paid ? t('storybookPage.book.failed.bookBody') : t('storybookPage.book.failed.previewBody')}
          </p>
          {book.retryable ? (
            <Button size="lg" className="mt-6 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug" onClick={() => void retry()} disabled={retrying}>
              {retrying ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
              {t('storybookPage.book.failed.retry')}
            </Button>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">{t('storybookPage.book.failed.contact', { email: SUPPORT_EMAIL })}</p>
          )}
        </StateCard>
        {paid && <PrivateLinkCard id={id} token={token} emphasize />}
        {!paid && book.redraw?.allowed && <div className="mx-auto max-w-xl">{redrawPanel}</div>}
        {!paid && secondaryActions(t('storybookPage.book.actions.deleteDraft'))}
      </div>
    );
  } else if (book.status === 'preview_ready') {
    const visiblePages = (book.pages || []).filter((page) => !page.locked && page.text);
    const lockedPages = (book.pages || []).filter((page) => page.locked);
    const illustrationCount = (book.pages || []).length || 10;
    content = (
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_23rem] lg:items-start">
        <div className="min-w-0 space-y-6" data-testid="storybook-preview">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
              {t('storybookPage.book.preview.badge')}
            </span>
          </div>
          <StorybookCover book={book} onMediaError={onMediaError} headingLevel={1} />
          {book.blurb && (
            <p className="ph-no-capture mx-auto max-w-xl text-center text-lg italic text-muted-foreground">{book.blurb}</p>
          )}
          <LikenessCard book={book} />
          {redrawPanel}
          <div className="space-y-4">
            {visiblePages.map((page) => (
              <StorybookPageText key={page.index} page={page} />
            ))}
          </div>
          {lockedPages.length > 0 && (
            <div className="relative min-h-[17rem] overflow-hidden rounded-3xl border border-border bg-card p-4 sm:p-6" data-testid="storybook-locked">
              <div aria-hidden="true" className="grid grid-cols-3 gap-3 opacity-70 blur-[3px] sm:grid-cols-4">
                {lockedPages.map((page) => (
                  <div key={page.index} className="flex aspect-[3/4] flex-col gap-1.5 rounded-xl bg-[#fffaf0] p-3 ring-1 ring-black/5">
                    <div className="h-1/2 rounded-lg bg-[linear-gradient(135deg,hsl(199_90%_80%),hsl(330_85%_85%))]" />
                    <div className="h-1.5 w-full rounded bg-foreground/15" />
                    <div className="h-1.5 w-4/5 rounded bg-foreground/15" />
                    <div className="h-1.5 w-3/5 rounded bg-foreground/15" />
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/55 p-6 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                  <Lock className="h-5 w-5" aria-hidden="true" />
                </span>
                <p className="mt-3 max-w-sm text-lg font-semibold">
                  {t('storybookPage.book.preview.lockedTitle', { pages: lockedPages.length, illustrations: illustrationCount })}
                </p>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t('storybookPage.book.preview.lockedBody')}</p>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <PurchasePanel
            book={book}
            countries={countries}
            shipping={shipping}
            buying={buying}
            onBuy={(product) => void buy(product)}
            paymentLanding={paymentLanding}
            title={t('storybookPage.book.purchase.title')}
            subtitle={t('storybookPage.book.purchase.subtitle')}
          />
          <PrivateLinkCard id={id} token={token} />
          {secondaryActions(t('storybookPage.book.actions.deleteDraft'))}
        </aside>
      </div>
    );
  } else if (book.status === 'proofing') {
    content = (
      <div className="space-y-8">
        <StorybookProof
          book={book}
          choices={proofChoices}
          onChoicesChange={setProofChoices}
          busy={proofBusy}
          onRedraw={sendProofRedraw}
          onApprove={approveProof}
          onMediaError={onMediaError}
          intro={book.likeness ? <LikenessCard book={book} /> : undefined}
        />

        <PrivateLinkCard id={id} token={token} />

        {/* A PDF owner can still add the printed book before approving. */}
        {!book.purchased?.print && book.offers?.print?.available && (
          <div className="mx-auto max-w-xl">
            <PurchasePanel
              book={{ ...book, offers: { pdf: null, print: book.offers.print } }}
              countries={countries}
              shipping={shipping}
              buying={buying}
              onBuy={(product) => void buy(product)}
              paymentLanding={paymentLanding}
              title={t('storybookPage.book.upsell.title')}
              subtitle={t(shipping ? 'storybookPage.book.upsell.bodyPaid' : 'storybookPage.book.upsell.body')}
            />
          </div>
        )}

        {/* Nothing is made or printed until the approval, so the book (and a
            print bought for it) can still be withdrawn. */}
        {secondaryActions(t('storybookPage.book.actions.deleteBook'))}
      </div>
    );
  } else if (book.status === 'generating' || book.status === 'assembling') {
    // After the parent flagged pictures in the proof: only those are being drawn again.
    const redrawingCount = book.status === 'generating' ? book.proof?.redrawing?.length ?? 0 : 0;
    const total = book.progress?.total || (book.pages || []).length + 1;
    const done = Math.min(total, book.progress?.done ?? 0);
    const percent = total ? Math.round((done / total) * 100) : 0;
    const tiles = [
      { key: 'cover', label: t('storybookPage.book.reader.cover'), url: storybookMediaUrl(book.coverUrl) },
      ...(book.pages || []).map((page) => ({
        key: `page-${page.index}`,
        label: t('storybookPage.book.pageLabel', { n: page.index + 1 }),
        url: storybookMediaUrl(page.imageUrl),
      })),
    ];
    content = (
      <div className="space-y-8" data-testid="storybook-illustrating">
        <div className="glass-card mx-auto max-w-2xl p-6 text-center sm:p-8">
          <p className="storybook-bob inline-block text-5xl" aria-hidden="true">🎨</p>
          <h1 className="mt-3 text-2xl font-semibold sm:text-3xl">
            {redrawingCount > 0
              ? t('storybookPage.book.proof.redrawing.title', { count: redrawingCount })
              : t('storybookPage.book.illustrating.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {redrawingCount > 0 ? t('storybookPage.book.proof.redrawing.subtitle') : t('storybookPage.book.illustrating.subtitle')}
          </p>
          <div className="mt-6 space-y-2">
            {book.status === 'assembling' ? (
              <>
                <IndeterminateBar label={t('storybookPage.book.illustrating.assembling')} />
                <p className="text-sm font-semibold" role="status">{t('storybookPage.book.illustrating.assembling')}</p>
              </>
            ) : (
              <>
                <Progress value={percent} className="h-3" aria-label={t('storybookPage.book.illustrating.progressAria')} />
                <p className="text-sm font-semibold tabular-nums" role="status">
                  {t('storybookPage.book.illustrating.progress', { done, total })}
                </p>
              </>
            )}
          </div>
        </div>

        <PrivateLinkCard id={id} token={token} emphasize />

        {/* Offers stay buyable while the pages are painted (a PDF owner can
            upgrade to print now instead of waiting for "ready"). */}
        {(book.offers?.pdf?.available || book.offers?.print?.available) && (
          <div className="mx-auto max-w-xl">
            <PurchasePanel
              book={book}
              countries={countries}
              shipping={shipping}
              buying={buying}
              onBuy={(product) => void buy(product)}
              paymentLanding={paymentLanding}
              title={purchased ? t('storybookPage.book.upsell.title') : t('storybookPage.book.purchase.title')}
              subtitle={
                purchased
                  ? t(shipping ? 'storybookPage.book.upsell.bodyPaid' : 'storybookPage.book.upsell.body')
                  : t('storybookPage.book.purchase.subtitle')
              }
            />
          </div>
        )}

        <ul className="ph-no-capture grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" aria-label={t('storybookPage.book.illustrating.galleryAria')}>
          {tiles.map((tile) => (
            <li key={tile.key} className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="aspect-square bg-muted">
                {tile.url ? (
                  <img src={tile.url} alt={tile.label} className="h-full w-full object-cover" onError={onMediaError} />
                ) : (
                  <div className="flex h-full animate-pulse flex-col items-center justify-center gap-2 bg-[linear-gradient(135deg,hsl(199_90%_93%),hsl(330_85%_95%))]">
                    <span className="text-3xl" aria-hidden="true">🖌️</span>
                    <span className="text-xs font-medium text-muted-foreground">{t('storybookPage.book.illustrating.pending')}</span>
                  </div>
                )}
              </div>
              <p className="px-3 py-2 text-center text-xs font-semibold">{tile.label}</p>
            </li>
          ))}
        </ul>

        {book.print && <StorybookPrintTimeline print={book.print} />}
      </div>
    );
  } else {
    // ready
    const pdfUrl = storybookMediaUrl(book.downloads?.pdfUrl);
    content = (
      <div className="space-y-10" data-testid="storybook-ready">
        <div className="relative overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,hsl(45_95%_86%),hsl(330_85%_92%)_50%,hsl(199_90%_90%))] px-6 py-10 text-center shadow-[var(--shadow-card)] sm:px-10">
          <p className="text-5xl" aria-hidden="true">🎉📚✨</p>
          <h1 className="mt-4 text-3xl font-semibold sm:text-4xl">{t('storybookPage.book.ready.title')}</h1>
          <p className="mx-auto mt-2 max-w-lg text-foreground/80">{t('storybookPage.book.ready.subtitle')}</p>
          {pdfUrl ? (
            <Button asChild size="xl" className="mt-6 rounded-full h-auto min-h-14 whitespace-normal py-3 text-center leading-snug">
              <a href={pdfUrl} download onClick={() => trackEvent('storybook_pdf_downloaded')} data-testid="storybook-download">
                <Download aria-hidden="true" />
                {t('storybookPage.book.ready.download')}
              </a>
            </Button>
          ) : (
            <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('storybookPage.book.ready.preparing')}
            </p>
          )}
          <p className="mt-3 text-xs text-foreground/70">{t('storybookPage.book.ready.downloadNote')}</p>
        </div>

        <PrivateLinkCard id={id} token={token} emphasize />

        {/* The storybook's public page, with the sharer's referral code: the
            book itself stays private behind its own link above. */}
        <ShareCard studio="storybook" tone="storybook" />

        <StorybookReader book={book} onMediaError={onMediaError} />

        <BookSpecPlate book={book} />

        {book.print && <StorybookPrintTimeline print={book.print} />}

        {!book.purchased?.print && book.offers?.print?.available && (
          <div className="mx-auto max-w-xl">
            <PurchasePanel
              book={{ ...book, offers: { pdf: null, print: book.offers.print } }}
              countries={countries}
              shipping={shipping}
              buying={buying}
              onBuy={(product) => void buy(product)}
              paymentLanding={paymentLanding}
              title={t('storybookPage.book.upsell.title')}
              subtitle={t(shipping ? 'storybookPage.book.upsell.bodyPaid' : 'storybookPage.book.upsell.body')}
            />
          </div>
        )}

        <div className="flex justify-center">{secondaryActions(t('storybookPage.book.actions.deleteBook'))}</div>
      </div>
    );
  }

  const awaiting = book ? awaitingPayment(book, awaitingRef.current) : false;

  return (
    <StorybookShell title={t('storybookPage.meta.bookTitle')}>
      <div className="ph-no-capture container mx-auto max-w-5xl px-4 py-8 sm:py-12">
        <div className="mb-6">
          <Link
            to="/storybook"
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('storybookPage.book.back')}
          </Link>
        </div>

        {banner && !deleted && (
          <div
            role="status"
            data-testid={`storybook-checkout-${banner}`}
            className={cn(
              'mb-6 flex items-start gap-3 rounded-2xl border p-4 text-sm',
              banner === 'success' ? 'border-emerald-600/30 bg-emerald-50 text-emerald-950' : 'border-border bg-card',
            )}
          >
            {banner === 'success' ? (
              <PartyPopper className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden="true" />
            ) : (
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                {banner === 'success'
                  ? t(checkoutSuccessKey(returnedTo))
                  : t('storybookPage.book.checkout.cancelled')}
              </p>
              {banner === 'success' && slowPayment && awaiting && (
                <p className="mt-1">{t('storybookPage.book.checkout.slow')}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="rounded-full p-1 opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('storybookPage.book.checkout.dismiss')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {book?.giftFrom && (
          <p className="mx-auto mb-6 flex w-fit max-w-full items-center gap-2 rounded-full bg-card px-4 py-2 text-sm font-semibold shadow-sm" data-testid="storybook-gift-from">
            <span aria-hidden="true">🎁</span>
            {t('storybookGift.book.from', { name: book.giftFrom })}
          </p>
        )}

        {content}
      </div>

      {book && token && photosEnabled && (
        <UpdatePhotosDialog
          open={updatingPhotos}
          onOpenChange={setUpdatingPhotos}
          book={book}
          config={config}
          onSubmit={submitNewPhotos}
        />
      )}

      <AlertDialog open={confirmDelete} onOpenChange={(open) => !deleting && setConfirmDelete(open)}>
        <AlertDialogContent className="storybook-theme w-[calc(100%-2rem)] rounded-2xl bg-background text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('storybookPage.book.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(book && deletesUnsentPrint(book) ? 'storybookPage.book.deleteDialog.bodyPrint' : 'storybookPage.book.deleteDialog.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={deleting} className="rounded-full">
              {t('storybookPage.book.deleteDialog.cancel')}
            </AlertDialogCancel>
            <Button variant="destructive" className="rounded-full" onClick={() => void remove()} disabled={deleting}>
              {deleting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
              {deleting ? t('storybookPage.book.deleteDialog.deleting') : t('storybookPage.book.deleteDialog.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StorybookShell>
  );
}

/**
 * The delivered book's spec plate (Studio Blueprint 05-A, 2026-10-16), filled
 * only from this order's own record: what it is, when it was made, what was
 * bought and whether the face crops are gone. A field the record lacks is
 * left off.
 */
function BookSpecPlate({ book }: { book: Book }) {
  const { t, i18n } = useTranslation();
  const made = book.createdAt ? new Date(book.createdAt) : null;
  const madeLabel = made && !Number.isNaN(made.getTime())
    ? made.toLocaleDateString(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const pdf = Boolean(book.purchased?.pdf);
  const print = Boolean(book.purchased?.print);
  const bought = pdf && print ? t('trust.spec.pdfAndPrint') : print ? t('trust.spec.print') : pdf ? t('trust.spec.pdf') : null;
  const pages = Array.isArray(book.pages) ? book.pages.length : 0;
  const photos = book.photosKept === false ? t('trust.spec.cropsDeleted') : book.photosKept === true ? t('trust.spec.cropsKept') : null;
  return (
    <SpecPlate
      title={t('trust.spec.title')}
      rows={[
        { label: t('trust.spec.studio'), value: t('trust.spec.storybooks') },
        { label: t('trust.spec.output'), value: pages > 0 ? t('trust.spec.pages', { count: pages }) : null },
        { label: t('trust.spec.made'), value: madeLabel },
        { label: t('trust.spec.bought'), value: bought },
        { label: t('trust.spec.photos'), value: photos },
      ]}
    />
  );
}
